import { useEffect, useRef } from 'react'
import { ensureTurnstileScript, getTurnstileSiteKey } from './helpers'
import { WidgetHost } from './styled'
import type { TurnstileWidgetProps } from './types'

/**
 * Explicit-render Cloudflare Turnstile widget for the booking guest form.
 *
 * @param {TurnstileWidgetProps} props - Token callback, theme, and reset signal.
 * @returns {JSX.Element} Host element for the Turnstile iframe.
 */
const TurnstileWidget = ({
  onTokenChange,
  theme = 'auto',
  resetSignal = 0,
}: TurnstileWidgetProps): JSX.Element => {
  const hostReference = useRef<HTMLDivElement | null>(null)
  const widgetIdReference = useRef<string | null>(null)
  const onTokenChangeReference = useRef(onTokenChange)

  useEffect(() => {
    onTokenChangeReference.current = onTokenChange
  }, [onTokenChange])

  useEffect(() => {
    let isCancelled = false

    const mountWidget = async (): Promise<void> => {
      try {
        await ensureTurnstileScript()
      } catch (error) {
        console.error('[Turnstile] script load failed:', error)
        onTokenChangeReference.current('')
        return
      }

      if (isCancelled || !hostReference.current || !window.turnstile) {
        return
      }

      if (widgetIdReference.current) {
        window.turnstile.remove(widgetIdReference.current)
        widgetIdReference.current = null
      }

      hostReference.current.innerHTML = ''

      widgetIdReference.current = window.turnstile.render(hostReference.current, {
        sitekey: getTurnstileSiteKey(),
        theme,
        action: 'turnstile-spin-v2',
        callback: (token: string) => {
          onTokenChangeReference.current(token)
        },
        'expired-callback': () => {
          onTokenChangeReference.current('')
        },
        'error-callback': () => {
          onTokenChangeReference.current('')
        },
      })
    }

    mountWidget()

    return () => {
      isCancelled = true
      if (widgetIdReference.current && window.turnstile) {
        window.turnstile.remove(widgetIdReference.current)
        widgetIdReference.current = null
      }
    }
  }, [theme])

  useEffect(() => {
    if (resetSignal === 0) {
      return
    }

    if (widgetIdReference.current && window.turnstile) {
      window.turnstile.reset(widgetIdReference.current)
      onTokenChangeReference.current('')
    }
  }, [resetSignal])

  return <WidgetHost ref={hostReference} data-action="turnstile-spin-v2" />
}

export default TurnstileWidget
